import { getFileDisplayState } from '../model/file-display-state';
import { getProgrammingProvider } from '../model/programming-service';

export interface MassOsTipoCategoriaOption {
  tipo: string;
  categorias: string[];
}

export interface MassOsFilterOptions {
  days: string[];
  typesOfService: string[];
  categoriesByType: MassOsTipoCategoriaOption[];
  services: string[];
  orderNumbers: Array<string | number>;
  clients: string[];
}

export interface MassOsFiltersState {
  day: string | null;
  typeOfService: string | null;
  categoryOfService: string | null;
  serviceName: string | null;
  client: string | null;
  orderNumber: string | null;
  estadoFile: string | null;
  startDate: Date | null;
  endDate: Date | null;
}

export const MASS_OS_FILE_STATES = [
  'APROBADO',
  'NO APROBADO',
  'POSTERGADO',
  'NUEVO PEDIDO',
  'REVISAR',
  'EN PROCESO',
  'CONFIRMADO',
  'RECONFIRMADO',
  'LAST MINUTE',
  'PROGRAMADO',
  'EN OPERACION',
  'OPERADO',
  'ANULADO',
  'CERRADO'
] as const;

/** dateRange: "DD/MM/YYYY - DD/MM/YYYY" (también admite puntos). */
export function computeMassOsServiceDate(
  dateRange: string | null | undefined,
  day: string | number | null | undefined
): Date | null {
  if (!dateRange || day == null || day === '') {
    return null;
  }

  const startRaw = String(dateRange).split('-')[0]?.trim();
  if (!startRaw) {
    return null;
  }

  const separator = startRaw.includes('.') ? '.' : '/';
  const [dayPart, monthPart, yearPart] = startRaw.split(separator).map(Number);
  if (!dayPart || !monthPart || !yearPart) {
    return null;
  }

  const start = new Date(yearPart, monthPart - 1, dayPart);
  if (Number.isNaN(start.getTime())) {
    return null;
  }

  start.setDate(start.getDate() + Number(day) - 1);
  return start;
}

export function enrichServicesForMassOs(services: any[], file: any): any[] {
  return (services ?? []).map((service: any) => {
    const dateRange = service.dateRange ?? file?.dateRange ?? null;
    const serviceDate = computeMassOsServiceDate(dateRange, service.day);

    return {
      ...service,
      checked: false,
      fileNumber: file?.fileNumber,
      fileName: file?.fileName,
      fileExtend: file?.fileExtend,
      dateRange,
      serviceDate,
      nombreProveedor: getProgrammingProvider(service)?.nombre ?? null,
      fileDisplayState: getFileDisplayState({
        pendingPreOpIncidents: file?.pendingPreOpIncidents,
        estadoFile: file?.estadoFile,
        estadoItinerario: file?.estadoItinerario,
        lastMinute: file?.lastMinute
      })
    };
  });
}

export function excludeSsffParentRows(services: any[]): any[] {
  return (services ?? []).filter(
    (item: any) => !(item.ssffproduct && item.productId)
  );
}

export function buildMassOsFilterOptions(services: any[]): MassOsFilterOptions {
  const list = services ?? [];
  const typesOfService = [
    ...new Set(list.map((item) => item.tipoServicio).filter(Boolean))
  ] as string[];

  const categoriesByType: MassOsTipoCategoriaOption[] = typesOfService.map(
    (tipo) => {
      const categorias = [
        ...new Set(
          list
            .filter((service) => service.tipoServicio === tipo)
            .map((service) => service.categoriaServicio)
            .filter(Boolean)
        )
      ] as string[];
      return { tipo, categorias };
    }
  );

  return {
    days: [...new Set(list.map((item) => item.day).filter(Boolean))] as string[],
    typesOfService,
    categoriesByType,
    services: [
      ...new Set(list.map((item) => item.nombreServicio).filter(Boolean))
    ] as string[],
    orderNumbers: [
      ...new Set(
        list
          .map((item) => item.order?.orderNumber)
          .filter((value) => value != null && value !== '')
      )
    ] as Array<string | number>,
    clients: [
      ...new Set(list.map((item) => item.fileName).filter(Boolean))
    ] as string[]
  };
}

export function getCategoriesForTipo(
  options: MassOsFilterOptions,
  tipo: string | null
): string[] {
  if (!tipo) {
    return [];
  }

  return (
    options.categoriesByType.find((entry) => entry.tipo === tipo)?.categorias ??
    []
  );
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function filterMassOsServices(
  services: any[],
  filters: MassOsFiltersState
): any[] {
  let data = [...(services ?? [])];

  if (filters.day) {
    data = data.filter((item) => String(item.day) === String(filters.day));
  }

  if (filters.serviceName) {
    data = data.filter((item) => item.nombreServicio === filters.serviceName);
  }

  if (filters.typeOfService) {
    data = data.filter(
      (item) => item.tipoServicio === filters.typeOfService
    );
  }

  if (filters.categoryOfService) {
    data = data.filter(
      (item) => item.categoriaServicio === filters.categoryOfService
    );
  }

  if (filters.client) {
    data = data.filter((item) => item.fileName === filters.client);
  }

  if (filters.orderNumber != null && filters.orderNumber !== '') {
    data = data.filter(
      (item) =>
        String(item.order?.orderNumber ?? '') === String(filters.orderNumber)
    );
  }

  if (filters.estadoFile) {
    data = data.filter(
      (item) =>
        item.estadoFile === filters.estadoFile ||
        item.fileDisplayState === filters.estadoFile
    );
  }

  if (filters.startDate && filters.endDate) {
    const start = startOfDay(filters.startDate);
    const end = startOfDay(filters.endDate);

    data = data.filter((item) => {
      const serviceDate: Date | null =
        item.serviceDate instanceof Date
          ? item.serviceDate
          : computeMassOsServiceDate(item.dateRange, item.day);

      if (!serviceDate || Number.isNaN(serviceDate.getTime())) {
        return false;
      }

      const day = startOfDay(serviceDate);
      return day >= start && day <= end;
    });
  }

  return data;
}

export function countSelectedMassOsRows(services: any[]): number {
  return (services ?? []).filter((row) => row.checked).length;
}
